import { ParamMap, Params } from '@angular/router';
import { FilterState } from '../filter-bar/filter-bar.component';
import { SavedViewFilters, filterStateToSavedView, deserializeFilterState, isRelativeDatePreset } from './saved-view.util';

// Задача 8: period, comparison and selected brands/media outlets/authors/topics/sentiments live
// in the URL as query params (not only in-memory FilterState), so that (1) moving between the 7
// Analytics screens keeps the same filter, (2) a link shared with a colleague on the same client
// opens on the exact same view (the backend still decides who can see what data - every
// /v1/client/analytics/** call resolves clientId from the caller's own JWT, never from the URL),
// (3) the browser's Back/Forward restores the previous filter, and (4) an invalid/unknown param
// is silently ignored, falling back to the current/default filter rather than erroring. Built on
// top of filterStateToSavedView/deserializeFilterState (Saved Views) so the relative-preset and
// compare-type recompute logic isn't duplicated a third time.

const ARRAY_FIELDS = ['brands', 'mediaTypes', 'mediaOutlets', 'authors', 'topics', 'sourceTypes', 'sentiments'] as const;
const COMPARE_TYPES: SavedViewFilters['compareType'][] = ['previous_period', 'same_period_last_year', 'custom', 'none'];
const BRAND_ROLES: SavedViewFilters['brandRole'][] = ['all', 'client', 'competition'];
const TOPIC_GROUPS: SavedViewFilters['topicGroup'][] = ['all', 'Industry', 'Corporate'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const FILTER_PARAM_KEYS = ['from', 'to', 'preset', 'compare', 'cfrom', 'cto', 'brandRole', 'topicGroup', ...ARRAY_FIELDS] as const;

export function filterStateToQueryParams(filters: FilterState, tab: string, analysisMetric?: string): Params {
	const periodType: 'FIXED' | 'RELATIVE' = isRelativeDatePreset(filters.datePresetKey) ? 'RELATIVE' : 'FIXED';
	const saved = filterStateToSavedView(filters, periodType);
	const params: Params = { tab: tab || null, metric: analysisMetric || null };
	params['preset'] = saved.presetKey || null;
	params['from'] = saved.presetKey ? null : saved.startDate || null;
	params['to'] = saved.presetKey ? null : saved.endDate || null;
	params['compare'] = saved.compareType && saved.compareType !== 'none' ? saved.compareType : null;
	params['cfrom'] = saved.compareStartDate || null;
	params['cto'] = saved.compareEndDate || null;
	params['brandRole'] = saved.brandRole && saved.brandRole !== 'all' ? saved.brandRole : null;
	params['topicGroup'] = saved.topicGroup && saved.topicGroup !== 'all' ? saved.topicGroup : null;
	for (const field of ARRAY_FIELDS) {
		const value = saved[field] as string[] | undefined;
		params[field] = value && value.length ? value.join(',') : null;
	}
	return params;
}

function splitList(value: string | null): string[] | undefined {
	if (value == null) return undefined;
	const trimmed = value.trim();
	if (!trimmed) return [];
	return trimmed
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean);
}

function validEnum<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
	return value != null && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

function validIsoDate(value: string | null): string | undefined {
	return value != null && ISO_DATE.test(value) ? value : undefined;
}

/** Whether the URL carries any of the filter-specific params this util understands - used to
 *  decide whether to touch the current FilterState at all (an unrelated query string, e.g. just
 *  `?tab=brands`, must never reset dates/brands/etc. back to today's default). */
export function hasFilterQueryParams(params: ParamMap): boolean {
	return FILTER_PARAM_KEYS.some((k) => params.has(k));
}

/** Rebuilds a FilterState from URL query params, falling back to `current` for every field the
 *  URL doesn't specify (or specifies with an invalid value) - never resets a field to a hardcoded
 *  default just because the URL happened to omit it. Returns `current` unchanged if the URL has
 *  no recognized filter param at all, or if something unexpected throws (malformed JSON edge
 *  case) - "неважечки параметри ... тивко се игнорираат (default филтер)". */
export function filterStateFromQueryParams(
	params: ParamMap,
	current: FilterState,
	lang?: string,
	vsLabel = 'vs',
	noComparisonLabel = 'No comparison',
	translateFn: (key: string) => string = (k) => k,
): FilterState {
	if (!hasFilterQueryParams(params)) return current;
	try {
		const periodType: 'FIXED' | 'RELATIVE' = isRelativeDatePreset(current.datePresetKey) ? 'RELATIVE' : 'FIXED';
		const base = filterStateToSavedView(current, periodType);

		const preset = params.get('preset');
		const from = validIsoDate(params.get('from'));
		const to = validIsoDate(params.get('to'));
		if (preset) {
			base.periodType = 'RELATIVE';
			base.presetKey = preset;
			base.startDate = undefined;
			base.endDate = undefined;
		} else if (from || to) {
			base.periodType = 'FIXED';
			base.presetKey = undefined;
			if (from) base.startDate = from;
			if (to) base.endDate = to;
		}

		const compare = validEnum(params.get('compare'), COMPARE_TYPES);
		if (compare) base.compareType = compare;
		const cfrom = validIsoDate(params.get('cfrom'));
		const cto = validIsoDate(params.get('cto'));
		if (cfrom) base.compareStartDate = cfrom;
		if (cto) base.compareEndDate = cto;

		const brandRole = validEnum(params.get('brandRole'), BRAND_ROLES);
		if (brandRole) base.brandRole = brandRole;
		const topicGroup = validEnum(params.get('topicGroup'), TOPIC_GROUPS);
		if (topicGroup) base.topicGroup = topicGroup;

		for (const field of ARRAY_FIELDS) {
			const list = splitList(params.get(field));
			if (list !== undefined) (base[field] as string[]) = list;
		}

		return deserializeFilterState(JSON.stringify(base), lang, vsLabel, noComparisonLabel, translateFn);
	} catch {
		return current;
	}
}
